import React, { useState, useRef, useEffect } from 'react';
import { Shield, QrCode, Play, AlertCircle, CheckCircle2, Terminal, Activity, Download, UploadCloud } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import ResilientImage from '../components/ResilientImage';

const API_BASE = 'https://appvpn.dabitdada.live';
const APP_API_BASE = 'https://api.gp-pay.net';

const buildInstallOrUpdateUrl = (downloadUrl) => (
    `intent://update/#Intent;scheme=gppay;package=com.soft.debitpay;` +
    `S.browser_fallback_url=${encodeURIComponent(downloadUrl)};end`
);

const AppInstall = () => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [latestRelease, setLatestRelease] = useState(null);
    const [releaseLoading, setReleaseLoading] = useState(true);
    const [releaseUploading, setReleaseUploading] = useState(false);
    const [releaseForm, setReleaseForm] = useState({
        versionName: '',
        versionCode: '',
        releaseNotes: '',
        forceUpdate: false,
        apk: null
    });
    
    useEffect(() => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
        const isMaster = localStorage.getItem('isMaster') === 'true';
        if (userInfo.role === 'Super Admin' || isMaster) {
            setIsAdmin(true);
        }
    }, []);

    const loadLatestRelease = async () => {
        setReleaseLoading(true);
        try {
            const response = await fetch(`${APP_API_BASE}/api/app-release/latest`, { cache: 'no-store' });
            const data = await response.json();
            setLatestRelease(response.ok && data.success ? data : null);
        } catch (_error) {
            setLatestRelease(null);
        } finally {
            setReleaseLoading(false);
        }
    };

    useEffect(() => {
        loadLatestRelease();
    }, []);

    const handleReleaseUpload = async (event) => {
        event.preventDefault();
        if (!releaseForm.apk || !releaseForm.versionName || !releaseForm.versionCode) {
            return toast.error('APK, version name and version code are required');
        }

        const body = new FormData();
        body.append('apk', releaseForm.apk);
        body.append('versionName', releaseForm.versionName);
        body.append('versionCode', releaseForm.versionCode);
        body.append('releaseNotes', releaseForm.releaseNotes);
        body.append('forceUpdate', String(releaseForm.forceUpdate));

        setReleaseUploading(true);
        try {
            const response = await fetch(`${APP_API_BASE}/api/app-release/latest`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                body
            });
            const data = await response.json();
            if (!response.ok || !data.success) throw new Error(data.message || 'Upload failed');
            setLatestRelease(data);
            setReleaseForm({ versionName: '', versionCode: '', releaseNotes: '', forceUpdate: false, apk: null });
            event.target.reset();
            toast.success(`GP-Pay ${data.versionName} is now the latest release`);
        } catch (error) {
            toast.error(error.message || 'APK upload failed');
        } finally {
            setReleaseUploading(false);
        }
    };

    const [qrData, setQrData] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isScannerDownloaded, setIsScannerDownloaded] = useState(false);
    const [pairIp, setPairIp] = useState('');
    const [pairCode, setPairCode] = useState('');
    const [isPairing, setIsPairing] = useState(false);
    const [isPaired, setIsPaired] = useState(false);
    const [connIp, setConnIp] = useState('');

    const [logs, setLogs] = useState(['> System ready. Awaiting input...']);
    const logBoxRef = useRef(null);

    const [overlay, setOverlay] = useState({
        show: false,
        title: 'Initializing',
        msg: 'Preparing secure tunnel...',
        progress: 0,
        icon: '🚀',
        error: false
    });

    const addLog = (msg, isSuccess = true) => {
        const time = new Date().toLocaleTimeString([], { hour12: false });
        const prefix = isSuccess ? '✅' : '❌';
        setLogs(prev => [...prev, `[${time}] ${prefix} ${msg}`]);
    };

    useEffect(() => {
        if (logBoxRef.current) {
            logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
        }
    }, [logs, overlay.show]);

    const handleGenerateQR = async () => {
        setIsGenerating(true);
        try {
            const res = await fetch(`${API_BASE}/generate-wg`, {
                method: 'POST',
                headers: { 'X-API-Key': 'AMER-SEC-INSTALL-2026-XQ9P' }
            });
            const data = await res.json();
            if (data.status === 'success') {
                setQrData(data);
                setConnIp(`${data.ip}:5555`);
                setPairIp(`${data.ip}:`);
                addLog('VPN tunnel established and QR ready.', true);
                toast.success('QR Code Generated!');
            } else {
                toast.error(data.message || 'Failed to generate QR');
                addLog(data.message, false);
            }
        } catch (error) {
            toast.error('Network Error. Is the Python server running?');
            addLog('Network Error connecting to setup server', false);
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePair = async () => {
        if (!pairIp || !pairCode) return toast.error('IP and Code required');
        setIsPairing(true);
        addLog('Initiating ADB pairing protocol...');
        try {
            const res = await fetch(`${API_BASE}/pair`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': 'AMER-SEC-INSTALL-2026-XQ9P'
                },
                body: JSON.stringify({ pair_ip: pairIp, code: pairCode })
            });
            const data = await res.json();
            if (data.status === 'success') {
                addLog(data.message, true);
                toast.success('Paired successfully!');
                setIsPaired(true);
            } else {
                addLog(data.message, false);
                toast.error('Pairing failed');
            }
        } catch (error) {
            toast.error('Network Error');
            addLog('Network Error during pairing', false);
        } finally {
            setIsPairing(false);
        }
    };

    const getFriendlyError = (msg) => {
        if (!msg) return null;
        
        const lowerMsg = msg.toLowerCase();

        // ১. Account Remove Error (Unknown Admin, IllegalArgumentException, already accounts - সবগুলোতেই এই মেসেজ দেখাবে)
        if (
            lowerMsg.includes('already some accounts') || 
            lowerMsg.includes('not allowed to set the device owner') ||
            lowerMsg.includes('unknown admin') || 
            lowerMsg.includes('illegalargumentexception') ||
            lowerMsg.includes('ensure no other accounts')
        ) {
            return (
                <div className="text-start p-2">
                    <p className="text-danger fw-bold d-flex align-items-center gap-2 mb-3" style={{fontSize: '14px', textTransform: 'uppercase'}}>
                        <AlertCircle size={18} /> অ্যাকাউন্ট ডিলিট করুন!
                    </p>
                    <div className="alert alert-danger p-3 rounded" style={{fontSize: '13px'}}>
                        <p className="fw-bold text-dark mb-2">ডিভাইস ওনার সেটআপ সম্পন্ন করা যাচ্ছে না।</p>
                        <p className="text-muted mb-2">দয়া করে আপনার মোবাইলের নিচের অ্যাকাউন্টগুলো সেটিংস থেকে রিমুভ (Remove) করুন:</p>
                        <ul className="text-dark fw-bold mb-2" style={{listStyleType: 'disc', paddingLeft: '20px'}}>
                            <li>Google / Gmail Account</li>
                            <li>WhatsApp, IMO</li>
                            <li>TikTok, Facebook ইত্যাদি</li>
                        </ul>
                        <p className="fw-bold text-danger mb-2">সব ধরনের অ্যাকাউন্ট রিমুভ করা বাধ্যতামূলক।</p>
                        <hr className="my-2"/>
                        <p className="text-muted fst-italic text-center mb-0" style={{fontSize: '11px'}}>* সব অ্যাকাউন্ট রিমুভ করার পর নিচে RETRY বাটনে ক্লিক করুন।</p>
                    </div>
                </div>
            );
        }

        // ২. Permission Setup Fail
        if (lowerMsg.includes('write_secure_settings') || lowerMsg.includes('grant_runtime_permissions')) {
            return (
                <div className="text-start p-2">
                    <p className="text-danger fw-bold d-flex align-items-center gap-2 mb-3" style={{fontSize: '14px', textTransform: 'uppercase'}}>
                        <AlertCircle size={18} /> পারমিশন সেটআপ ফেইল!
                    </p>
                    <div className="alert alert-danger p-3 rounded" style={{fontSize: '12px'}}>
                        <p className="d-flex justify-content-between mb-2"><span className="text-muted">১. Oppo/Realme:</span> <span className="fw-bold text-dark">Permission Monitor → OFF</span></p>
                        <p className="d-flex justify-content-between mb-2"><span className="text-muted">২. Xiaomi/Poco:</span> <span className="fw-bold text-dark">USB Security Settings → ON</span></p>
                        <p className="d-flex justify-content-between mb-2"><span className="text-muted">৩. Vivo:</span> <span className="fw-bold text-dark">USB Simulated Input → ON</span></p>
                        <hr className="my-2"/>
                        <p className="text-muted fst-italic text-center mb-0" style={{fontSize: '11px'}}>* ফোনের সেটিংস পরিবর্তন করে নিচে RETRY বাটনে ক্লিক করুন।</p>
                    </div>
                </div>
            );
        }

        // ৩. Device Disconnected
        if (lowerMsg.includes('device not found') || lowerMsg.includes('offline')) {
            return (
                <div className="text-center py-4">
                    <p className="text-danger fw-bold mb-1">মোবাইল কানেকশন বিচ্ছিন্ন!</p>
                    <p className="text-muted mb-0" style={{fontSize: '12px'}}>USB ক্যাবলটি ঠিকমতো লাগানো আছে কিচেতন চেক করে আবার ট্রাই করুন।</p>
                </div>
            );
        }

        // ৪. Catch-all (লগ পুরোপুরি হাইড করার জন্য)
        return (
            <div className="text-center py-4">
                <p className="text-danger fw-bold mb-1">অজানা সমস্যা হয়েছে!</p>
                <p className="text-muted mb-0" style={{fontSize: '12px'}}>দয়া করে ফোনের কানেকশন চেক করে আবার চেষ্টা করুন।</p>
            </div>
        );
    };

    const handleSetup = async () => {
        if (!connIp) return toast.error('Connection IP required');

        setOverlay({ show: true, title: 'Automation', msg: 'Processing setup commands...', progress: 15, icon: '⚡', error: false });
        addLog(`Processing setup for target: ${connIp}`);

        let p = 20;
        const timer = setInterval(() => {
            if (p < 95) {
                p += 2;
                setOverlay(prev => ({ ...prev, msg: '📦 Running ADB commands...', progress: p }));
            }
        }, 1000);

        try {
            const res = await fetch(`${API_BASE}/run-setup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': 'AMER-SEC-INSTALL-2026-XQ9P'
                },
                body: JSON.stringify({ conn_ip: connIp, pub_key: qrData?.pub_key })
            });

            clearInterval(timer);
            const data = await res.json();

            if (data.status === 'success') {
                setOverlay(prev => ({ ...prev, title: 'Finalizing', msg: 'Granting Device Owner...', progress: 98, icon: '🛡️' }));
                data.logs.forEach(l => addLog(l, true));

                setTimeout(() => {
                    setOverlay(prev => ({ ...prev, title: 'Done All', msg: 'App install finished successfully.', progress: 100, icon: '🎉' }));
                    setTimeout(() => {
                        setOverlay(prev => ({ ...prev, show: false }));
                        setQrData(null); 
                        addLog('FULL SETUP FINISHED. VPN DISCONNECTED.', true);
                        toast.success('Setup Completed Successfully!');
                    }, 2500);
                }, 1000);
            } else {
                setOverlay(prev => ({
                    ...prev,
                    title: 'Setup Failed',
                    msg: data.message, 
                    progress: 100,
                    icon: '❌',
                    error: true
                }));
                addLog(`Setup Error: ${data.message}`, false);
            }
        } catch (error) {
            clearInterval(timer);
            setOverlay(prev => ({
                ...prev,
                title: 'Network Error',
                msg: 'Server unreachable. Check internet/VPN.',
                progress: 100,
                icon: '⚠️',
                error: true
            }));
            addLog('Network error during setup', false);
        }
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-4">
                
                {/* Header Area */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0 d-flex align-items-center gap-2">
                        <Shield size={20} className="text-primary"/> Amer Security Device Setup
                    </h5>
                    {isAdmin && <div className="count-badge">Admin <span>Mode</span></div>}
                </div>

                {/* Main Content */}
                <div className="container-fluid p-0">
                    <div className="filter-card shadow-sm border-0 mb-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                            <div>
                                <h6 className="field-label mb-1" style={{color: '#0284c7', fontSize: '14px'}}>GP-Pay Latest App</h6>
                                <div className="text-muted" style={{fontSize: '13px'}}>
                                    {releaseLoading
                                        ? 'Checking latest release...'
                                        : latestRelease
                                            ? `Version ${latestRelease.versionName} (Code ${latestRelease.versionCode})`
                                            : 'No release uploaded yet'}
                                </div>
                            </div>
                            {latestRelease && (
                                <div className="d-flex flex-wrap gap-2">
                                    <a href={latestRelease.downloadUrl} className="btn btn-outline-primary fw-bold px-4 py-2 d-flex align-items-center gap-2">
                                        <Download size={18} /> Download APK
                                    </a>
                                    <a href={buildInstallOrUpdateUrl(latestRelease.downloadUrl)} className="btn btn-apply fw-bold px-4 py-2 d-flex align-items-center gap-2">
                                        <Play size={18} /> Install / Update
                                    </a>
                                </div>
                            )}
                        </div>

                        {latestRelease && (
                            <div className="row g-3 align-items-center mb-3">
                                <div className="col-auto">
                                    <div className="qr-container bg-white p-2 rounded border">
                                        <ResilientImage
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(latestRelease.downloadUrl)}`}
                                            alt="GP-Pay download QR"
                                            style={{width: '120px', height: '120px'}}
                                        />
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="small text-muted mb-1">Published: {new Date(latestRelease.publishedAt).toLocaleString()}</div>
                                    {latestRelease.releaseNotes && <div className="small text-dark mb-1">{latestRelease.releaseNotes}</div>}
                                    <div className="small text-muted font-monospace">SHA-256: {latestRelease.sha256}</div>
                                </div>
                            </div>
                        )}

                        {isAdmin && (
                            <form onSubmit={handleReleaseUpload} className="border-top pt-3">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <UploadCloud size={18} className="text-primary" />
                                    <strong style={{fontSize: '14px'}}>Publish New GP-Pay APK</strong>
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="field-label">Version name</label>
                                        <input className="modern-input" placeholder="9.2" value={releaseForm.versionName} onChange={e => setReleaseForm(prev => ({...prev, versionName: e.target.value}))} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="field-label">Version code</label>
                                        <input className="modern-input" type="number" min="1" placeholder="312" value={releaseForm.versionCode} onChange={e => setReleaseForm(prev => ({...prev, versionCode: e.target.value}))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="field-label">APK file</label>
                                        <input className="modern-input" type="file" accept=".apk,application/vnd.android.package-archive" onChange={e => setReleaseForm(prev => ({...prev, apk: e.target.files?.[0] || null}))} />
                                    </div>
                                    <div className="col-md-9">
                                        <label className="field-label">Update message</label>
                                        <input className="modern-input" placeholder="What is new in this version" value={releaseForm.releaseNotes} onChange={e => setReleaseForm(prev => ({...prev, releaseNotes: e.target.value}))} />
                                    </div>
                                    <div className="col-md-3 d-flex align-items-end">
                                        <button type="submit" disabled={releaseUploading} className="btn btn-primary fw-bold w-100 py-2">
                                            {releaseUploading ? 'Publishing...' : 'Publish Update'}
                                        </button>
                                    </div>
                                    <div className="col-12">
                                        <label className="d-flex align-items-center gap-2 small">
                                            <input type="checkbox" checked={releaseForm.forceUpdate} onChange={e => setReleaseForm(prev => ({...prev, forceUpdate: e.target.checked}))} />
                                            Require clients to install this update before continuing
                                        </label>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>

                    {!isScannerDownloaded ? (
                        <div className="row justify-content-center">
                            <div className="col-md-6">
                                <div className="filter-card shadow-sm border-0 mb-4 text-center">
                                    <div className="d-flex align-items-center justify-content-center mb-3">
                                        <span className="step-badge">01</span>
                                        <h6 className="field-label m-0 text-start ps-2" style={{color: '#0284c7', fontSize: '14px'}}>স্ক্যানার অ্যাপ ডাউনলোড (Download)</h6>
                                    </div>
                                    <p className="text-muted mb-4" style={{fontSize: '13px'}}>নিচের কিউআর কোডটি স্ক্যান করে অ্যাপটি ডাউনলোড এবং ইন্সটল করুন।</p>
                                    
                                    <div className="qr-container bg-white p-3 rounded d-inline-block border shadow-sm mb-4">
                                        <ResilientImage src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://appvpn.dabitdada.live/app-release.apk" alt="Download Scanner QR" style={{width: '180px', height: '180px'}} />
                                    </div>
                                    
                                    <button onClick={() => setIsScannerDownloaded(true)} className="btn btn-apply fw-bold w-100 py-3 d-flex align-items-center justify-content-center gap-2">
                                        <CheckCircle2 size={20} /> ডাউনলোড সম্পন্ন (DONE)
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            {/* Step 2: WireGuard */}
                            <div className="col-lg-5 mb-4">
                                <div className="filter-card shadow-sm border-0 h-100 text-center d-flex flex-column align-items-center">
                                    <div className="d-flex align-items-center justify-content-center mb-4 w-100">
                                        <span className="step-badge" style={{background: '#e0e7ff', color: '#4f46e5'}}>02</span>
                                        <h6 className="field-label m-0 text-start ps-2 w-100" style={{color: '#4f46e5', fontSize: '14px'}}>স্ক্যানার অ্যাপ দিয়ে কানেক্ট করুন</h6>
                                    </div>
                                    
                                    <button onClick={handleGenerateQR} disabled={isGenerating} className="btn btn-primary fw-bold w-100 py-3 mb-4 d-flex align-items-center justify-content-center gap-2" style={{background: '#4f46e5', border: 'none'}}>
                                        <QrCode size={20} /> {isGenerating ? 'জেনারেট হচ্ছে...' : 'QR কোড তৈরি করুন'}
                                    </button>
                                    
                                    {qrData && (
                                        <div className="qr-container bg-white p-3 rounded border shadow-sm w-100 text-center">
                                            <ResilientImage src={`data:image/png;base64,${qrData.qr}`} alt="QR Code" style={{width: '160px', height: '160px', margin: '0 auto'}} />
                                            <div className="mt-3 p-2 rounded" style={{background: '#f1f5f9', fontSize: '12px', fontWeight: 'bold', color: '#334155', fontFamily: 'monospace'}}>
                                                Assigned IP: {qrData.ip}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Step 3: Automation */}
                            <div className="col-lg-7 mb-4">
                                <div className="filter-card shadow-sm border-0 h-100">
                                    <div className="d-flex align-items-center mb-4">
                                        <span className="step-badge" style={{background: '#dcfce7', color: '#16a34a'}}>03</span>
                                        <h6 className="field-label m-0 text-start ps-2" style={{color: '#16a34a', fontSize: '14px'}}>ডিভাইজ পেয়ারিং এবং সেটআপ</h6>
                                    </div>
                                    
                                    <div className="row g-3 mb-4">
                                        <div className="col-sm-6">
                                            <label className="field-label">IP address & Port</label>
                                            <input type="text" placeholder="10.8.0.x:xxxxx" value={pairIp} onChange={e => setPairIp(e.target.value)} className="modern-input text-center font-monospace" />
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="field-label">Pairing Code</label>
                                            <input type="text" placeholder="000000" value={pairCode} onChange={e => setPairCode(e.target.value)} className="modern-input text-center font-monospace" />
                                        </div>
                                    </div>
                                    
                                    <button onClick={handlePair} disabled={isPairing} className="btn btn-outline-success fw-bold w-100 py-2 mb-4">
                                        {isPairing ? 'Pairing...' : 'পেয়ার করুন (EXECUTE PAIRING)'}
                                    </button>

                                    {isPaired && (
                                        <div className="border-top pt-4">
                                            <div className="mb-3">
                                                <label className="field-label">Target IP</label>
                                                <input type="text" value={connIp} onChange={e => setConnIp(e.target.value)} className="modern-input text-center font-monospace fw-bold text-primary" style={{fontSize: '15px'}} />
                                            </div>
                                            <button onClick={handleSetup} className="btn btn-apply fw-bold w-100 py-3 d-flex justify-content-center align-items-center gap-2">
                                                <Play size={18} fill="currentColor" /> সেটআপ শুরু করুন
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Terminal for Admin */}
                    {isAdmin && (
                        <div className="filter-card shadow-sm border-0 bg-dark text-light mt-2">
                            <h6 className="field-label mb-3 text-light d-flex align-items-center gap-2">
                                <Terminal size={14} /> লাইভ টার্মিনাল (Terminal Output)
                            </h6>
                            <div ref={logBoxRef} className="terminal-box">
                                {logs.map((log, i) => (
                                    <div key={i} className={log.includes('✅') ? 'text-success' : log.includes('❌') ? 'text-danger' : ''}>
                                        {log}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom Overlay / Modal for Loading */}
            {overlay.show && (
                <div className="overlay-modal-bg">
                    <div className="overlay-modal-content shadow-lg">
                        <div className="display-4 mb-3 modal-icon">{overlay.icon}</div>
                        <h4 className="fw-bold mb-3">{overlay.title}</h4>
                        
                        {overlay.error ? (
                            <div>{getFriendlyError(overlay.msg)}</div>
                        ) : (
                            <div className="bg-light p-3 rounded border mb-3 text-primary font-monospace" style={{fontSize: '12px', maxHeight: '100px', overflowY: 'auto'}}>
                                {overlay.msg}
                            </div>
                        )}
                        
                        <div className="progress mb-4" style={{height: '8px'}}>
                            <div className={`progress-bar progress-bar-striped progress-bar-animated ${overlay.error ? 'bg-danger' : 'bg-primary'}`} role="progressbar" style={{ width: `${overlay.progress}%` }}></div>
                        </div>
                        
                        <div className="d-flex gap-2">
                            {overlay.error ? (
                                <>
                                    <button onClick={() => handleSetup()} className="btn btn-primary fw-bold w-50 py-2 d-flex align-items-center justify-content-center gap-2">
                                        <Activity size={16} /> RETRY
                                    </button>
                                    <button onClick={() => setOverlay(prev => ({ ...prev, show: false }))} className="btn btn-secondary fw-bold w-50 py-2">
                                        CLOSE
                                    </button>
                                </>
                            ) : (
                                <div className="text-muted text-center w-100 fw-bold blinking-text" style={{fontSize: '11px', letterSpacing: '1px'}}>
                                    অপেক্ষা করুন... সিস্টেম কাজ করছে
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* CSS Styles */}
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 25px; border-radius: 12px; }
                .field-label { font-size: 11px; font-weight: 800; color: #64748b; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.5px;}
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 12px; border-radius: 8px; font-size: 14px; width: 100%; outline: none; transition: 0.2s; }
                .modern-input:focus { border-color: #0284c7; background: #fff; box-shadow: 0 0 0 3px rgba(2,132,199,0.1); }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 14px; transition: 0.3s; border: none; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); color: white;}
                
                .step-badge { width: 32px; height: 32px; border-radius: 50%; background: #e0f2fe; color: #0284c7; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 1px solid rgba(2, 132, 199, 0.2); }
                
                .terminal-box { height: 180px; overflow-y: auto; font-size: 12px; font-family: monospace; color: #94a3b8; padding-right: 10px;}
                .terminal-box::-webkit-scrollbar { width: 6px; }
                .terminal-box::-webkit-scrollbar-thumb { background: #475569; border-radius: 10px; }

                /* Overlay CSS */
                .overlay-modal-bg { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.8); backdrop-filter: blur(4px); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 20px;}
                .overlay-modal-content { background: #ffffff; width: 100%; max-width: 450px; border-radius: 20px; padding: 30px; text-align: center; animation: popIn 0.3s ease-out; }
                .modal-icon { animation: bounce 2s infinite; }
                .blinking-text { animation: blinker 1.5s linear infinite; }
                
                @keyframes popIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
                @keyframes blinker { 50% { opacity: 0.4; } }
            `}</style>
        </Layout>
    );
};

export default AppInstall;
